// File: src/components/forms/PasswordField/PasswordField.tsx
// Purpose: Form input component

import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import  { TextField, TextFieldProps } from "../TextField";
import styled from "styled-components";

// 🎨 **Password Strength Bar**
const StrengthBar = styled.div<{ strength: number }>`
  height: 6px;
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing.xs};
  background: ${({ strength, theme }) =>
    strength < 2 ? theme.colors.error :
    strength < 4 ? theme.colors.warning :
    theme.colors.success};
  border-radius: ${({ theme }) => theme.radii.sm};
  transition: 0.3s ease-in-out;
`;

export const PasswordField: React.FC<TextFieldProps> = (props) => {
  const [visible, setVisible] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [strength, setStrength] = useState(0);
  const [password, setPassword] = useState(props.value || "");

  // ✅ **Modern Password Strength Validation**
  const validatePassword = (value: string) => {
    if (!touched) return undefined; // Only validate after interaction
    if (!value) return "Password is required";

    let score = 0;
    if (value.length >= 8) score++; // ✅ Base length
    if (value.length >= 12) score++; // ✅ Extra length
    if (/[A-Z]/.test(value)) score++; // ✅ Uppercase
    if (/[0-9]/.test(value)) score++; // ✅ Number
    if (/[^A-Za-z0-9]/.test(value)) score++; // ✅ Special character

    setStrength(score);

    if (score < 2) return "Password is too weak";
    if (score < 4) return "Consider making it stronger";
    return undefined;
  };

  // **Handler for Password Changes**
  useEffect(() => {
    const validationError = validatePassword(password);
    setError(validationError);
  }, [password]);

  return (
    <>
      <TextField
        {...props}
        type={visible ? "text" : "password"}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onBlur={() => setTouched(true)}
        error={error} // ✅ Now error is **undefined** when no validation required
        icon={visible ? EyeOff : Eye}
        onIconClick={() => setVisible((prev) => !prev)}
      />
      {touched && password && <StrengthBar strength={strength} />} {/* ✅ Strength meter */}
    </>
  );
};